import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { GetProp, TableColumnsType, TableProps, TransferProps } from 'antd';
import { Modal, Table, Transfer } from 'antd';
import { FilterValue, SorterResult } from 'antd/es/table/interface';
import { PosterInterface } from 'content/type';
import React, { Key, useEffect, useState } from 'react';
import './Transfer.css';
import { TransferSearch } from './TransferSearch';

type TransferItem = GetProp<TransferProps, 'dataSource'>[number];
type TableRowSelection<T extends object> = TableProps<T>['rowSelection'];

export interface TransferDataSourceInterface {
  id: number;
  key: number;
  name: string;
  disabled: boolean;
  type?: 'series' | 'season' | 'content';
  poster?: PosterInterface;
}

export interface TransferDataHandlerInterface {
  fetchSourceData: (
    query: Record<string, string>
  ) => Promise<{ data: TransferDataSourceInterface[]; count: number }>;
  fetchTargetData: (
    query: Record<string, string>
  ) => Promise<{ data: TransferDataSourceInterface[]; count: number }>;
  createWithParentId: (ids: number[]) => Promise<void>;
  deleteWithParentId: (ids: number[]) => Promise<void>;
}

interface TableTransferProps<T extends TransferDataSourceInterface>
  extends TransferProps<TransferItem> {
  dataHandler: TransferDataHandlerInterface;
  columns: TableColumnsType<T>;
  size?: 'small' | 'middle' | 'large';
  filterOptions?: { label: string; value: string }[];
  onFilterChange?: (value: string) => void;
  filterValue?: string;
  /**
   * 순서 관련 커스터마이징 옵션
   *
   * @example
   * // episode_order 필드 사용
   * orderOptions={{
   *   fieldName: 'episode_order',
   *   orderByField: 'episode_order',
   *   onOrderChange: async (orders) => {
   *     await Promise.all(orders.map(({ id, episode_order }) =>
   *       updateEpisode(id, { episode_order })
   *     ));
   *   }
   * }}
   *
   * @example
   * // episode_number 필드 사용
   * orderOptions={{
   *   fieldName: 'episode_number',
   *   orderByField: 'episode_number',
   *   onOrderChange: async (orders) => {
   *     await Promise.all(orders.map(({ id, episode_number }) =>
   *       updateEpisode(id, { episode_number })
   *     ));
   *   }
   * }}
   */
  orderOptions?: {
    // 순서 필드명 (예: 'episode_order', 'episode_number', 'order' 등)
    fieldName?: string;
    // 정렬 기준 필드명 (기본값: fieldName과 동일)
    orderByField?: string;
    // 순서 변경 핸들러 (각 페이지에서 커스터마이징 가능)
    onOrderChange?: (orders: { id: number; [key: string]: number }[]) => Promise<void>;
  };
  // 하위 호환성을 위한 기존 prop (deprecated)
  onOrderChange?: (
    orders: { id: number; episode_order: number }[] | { id: number; [key: string]: number }[]
  ) => Promise<void>;
  /**
   * unlink 기능 비활성화 옵션
   * true로 설정하면 오른쪽에서 왼쪽으로 이동하는 버튼이 비활성화됩니다.
   */
  disableUnlink?: boolean;
}

const SortableRow = ({
  children,
  id,
  ...props
}: {
  children: React.ReactNode;
  id: number;
  [key: string]: any;
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging, over } =
    useSortable({
      id,
    });

  const [isPressed, setIsPressed] = React.useState(false);

  // isDragging이 true가 되면 pressed 상태 해제
  React.useEffect(() => {
    if (isDragging) {
      setIsPressed(false);
    }
  }, [isDragging]);

  // 전역 마우스 이벤트로 pressed 상태 관리
  React.useEffect(() => {
    if (isPressed) {
      const handleGlobalMouseUp = () => {
        setIsPressed(false);
      };
      window.addEventListener('mouseup', handleGlobalMouseUp);
      window.addEventListener('pointerup', handleGlobalMouseUp);
      return () => {
        window.removeEventListener('mouseup', handleGlobalMouseUp);
        window.removeEventListener('pointerup', handleGlobalMouseUp);
      };
    }
  }, [isPressed]);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: isDragging ? 'none' : transition,
    opacity: isDragging ? 0.3 : isPressed ? 0.95 : 1,
    cursor: 'move',
    position: 'relative' as const,
    zIndex: isDragging ? 1 : isPressed ? 999 : 1,
    backgroundColor: isPressed && !isDragging ? '#e6f7ff' : undefined,
    border: isPressed && !isDragging ? '1px solid #1890ff' : undefined,
    boxShadow:
      isPressed && !isDragging
        ? '0 2px 8px rgba(24, 144, 255, 0.2), inset 0 0 0 1px #1890ff'
        : undefined,
  };

  // 체크박스나 링크 클릭 시 드래그 방지를 위한 핸들러
  const handlePointerDown = (e: React.PointerEvent) => {
    const target = e.target as HTMLElement;
    if (
      target.tagName === 'INPUT' ||
      target.tagName === 'A' ||
      target.closest('input') ||
      target.closest('a') ||
      target.closest('.ant-checkbox-wrapper') ||
      target.closest('.ant-checkbox')
    ) {
      e.stopPropagation();
      return;
    }
    // 클릭 시작 시 상태 설정
    setIsPressed(true);
    // listeners의 onPointerDown 호출
    if (listeners?.onPointerDown) {
      listeners.onPointerDown(e as any);
    }
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (
      target.tagName === 'INPUT' ||
      target.tagName === 'A' ||
      target.closest('input') ||
      target.closest('a') ||
      target.closest('.ant-checkbox-wrapper') ||
      target.closest('.ant-checkbox')
    ) {
      e.stopPropagation();
      return;
    }
    // 클릭 시작 시 상태 설정
    setIsPressed(true);
  };

  const handlePointerUp = () => {
    setIsPressed(false);
  };

  const handlePointerCancel = () => {
    setIsPressed(false);
  };

  // listeners에서 onPointerDown을 커스텀 핸들러로 교체
  const customListeners = {
    ...listeners,
    onPointerDown: handlePointerDown,
    onPointerUp: handlePointerUp,
    onPointerCancel: handlePointerCancel,
  };

  // 클래스명 동적 생성 - 드롭 위치 표시
  const isOver = over && over.id !== id && !isDragging;
  const isOverTop = isOver; // 위쪽에 드롭할 때
  const className = [
    'sortable-row',
    isDragging ? 'dragging' : '',
    isPressed && !isDragging ? 'pressed' : '',
    isOverTop ? 'drag-over' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className={className}
      {...props}
      {...attributes}
      {...customListeners}
      onMouseDown={handleMouseDown}
    >
      {children}
    </tr>
  );
};

const CustomTransfer = <T extends TransferDataSourceInterface>(
  props: TableTransferProps<T>
): React.ReactElement => {
  const {
    columns,
    dataHandler,
    size = 'large',
    filterOptions,
    onFilterChange,
    filterValue,
    orderOptions,
    onOrderChange: legacyOnOrderChange,
    disableUnlink = false,
    ...restProps
  } = props;

  // 순서 관련 설정 (orderOptions 우선, 없으면 기본값 사용)
  const orderFieldName = orderOptions?.fieldName || 'episode_order';
  const orderByField = orderOptions?.orderByField || orderFieldName;
  const onOrderChangeHandler = orderOptions?.onOrderChange || legacyOnOrderChange;
  const [pageSize, setPageSize] = useState(10);
  const [leftSelectedKeys, setLeftSelectedKeys] = useState<number[]>([]);
  const [rightSelectedKeys, setRightSelectedKeys] = useState<number[]>([]);

  const [transferDataSource, setTransferDataSource] = useState<TransferDataSourceInterface[]>([]);
  const [transferDataSourceTotal, setTransferDataSourceTotal] = useState(0);
  const [targetSource, setTargetSource] = useState<TransferDataSourceInterface[]>([]);
  const [targetSourceTotal, setTargetSourceTotal] = useState(0);

  const [targetPage, setTargetPage] = React.useState<number>(1);
  const [sourcePage, setSourcePage] = React.useState<number>(1);

  const [targetSearchTerm, setTargetSearchTerm] = React.useState<string>('');
  const [sourceSearchTerm, setSourceSearchTerm] = React.useState<string>('');

  const [targetContentType, setTargetContentType] = React.useState<string>(
    'tv-series,movie,clip,trailer'
  );
  const [sourceContentType, setSourceContentType] = React.useState<string>(
    'tv-series,movie,clip,trailer'
  );

  const [targetOrder, setTargetOrder] = React.useState<string>('id');
  const [sourceOrder, setSourceOrder] = React.useState<string>('id');

  const [targetSortField, setTargetSortField] = React.useState<Key>('');
  const [sourceSortField, setSourceSortField] = React.useState<Key>('');

  const [isSourceLoading, setIsSourceLoading] = React.useState(false);
  const [isTargetLoading, setIsTargetLoading] = React.useState(false);

  const [refreshKey, setRefreshKey] = useState(0);
  const [shouldUpdateOrder, setShouldUpdateOrder] = useState(false);
  const [activeId, setActiveId] = useState<number | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = async (
    event: DragEndEvent,
    direction: 'left' | 'right',
    currentDataSource: TransferDataSourceInterface[]
  ) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    if (direction === 'right') {
      // 현재 페이지의 데이터에서 인덱스 찾기
      const oldIndex = currentDataSource.findIndex(item => item.id === active.id);
      const newIndex = currentDataSource.findIndex(item => item.id === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        // 현재 페이지 데이터 재정렬
        const newCurrentPageData = arrayMove(currentDataSource, oldIndex, newIndex);

        // 전체 targetSource 업데이트 (현재 페이지 데이터 교체)
        const startIndex = (targetPage - 1) * pageSize;
        const newTargetSource = [...targetSource];
        newCurrentPageData.forEach((item, index) => {
          newTargetSource[startIndex + index] = item;
        });

        // setTargetSource(newTargetSource);

        // 전체 데이터를 가져와서 순서 업데이트
        try {
          const allDataQuery = {
            page: '1',
            page_size: '1000', // 충분히 큰 값으로 전체 데이터 가져오기
            search: targetSearchTerm,
            content_type: targetContentType,
            order_by: 'episode_order',
            order_direction: 'asc',
          };
          const { data: allData } = await dataHandler.fetchTargetData(allDataQuery);

          // 드래그된 항목의 새로운 위치 계산
          const draggedItem = allData.find(item => item.id === active.id);
          const targetItem = allData.find(item => item.id === over.id);

          if (draggedItem && targetItem) {
            const allDataOldIndex = allData.findIndex(item => item.id === active.id);
            const allDataNewIndex = allData.findIndex(item => item.id === over.id);
            const reorderedAllData = arrayMove(allData, allDataOldIndex, allDataNewIndex);

            // 전체 순서 업데이트
            const updatedOrders = reorderedAllData.map(
              (item: TransferDataSourceInterface, index: number) => ({
                id: item.id,
                [orderFieldName]: index + 1,
              })
            );
            if (onOrderChangeHandler) {
              await onOrderChangeHandler(updatedOrders as { id: number; [key: string]: number }[]);
              // order 업데이트 후 target 데이터 refresh
              setRefreshKey(prev => prev + 1);
            }
          }
        } catch (error) {
          console.error('Failed to update episode order after drag:', error);
        }
      }
    }
  };

  const onPageChange = async (
    direction: 'left' | 'right',
    page: number,
    pageSize: number,
    filters: Record<string, FilterValue | null>,
    sorter?: SorterResult<TransferDataSourceInterface>
  ): Promise<void> => {
    setPageSize(pageSize);
    const contentType: string = (filters.content_type as unknown as string) || 'tv-series,movie';
    if (direction === 'right') {
      setTargetPage(page);
      setTargetContentType(contentType);

      if (sorter && sorter.field) {
        setTargetOrder(sorter.order || '');
        setTargetSortField(Array.isArray(sorter.field) ? sorter.field[0] : sorter.field);
      }
      return;
    } else {
      setSourcePage(page);
      setSourceContentType(contentType);

      if (sorter && sorter.field) {
        setSourceOrder(sorter.order || '');
        setSourceSortField(Array.isArray(sorter.field) ? sorter.field[0] : sorter.field);
      }
    }
  };

  const onSearchHandler = async (direction: 'left' | 'right', value: string): Promise<void> => {
    if (direction === 'right') {
      setTargetSearchTerm(value);
      setTargetPage(1);
    } else {
      setSourceSearchTerm(value);
      setSourcePage(1);
    }
  };

  const onChange: TransferProps['onChange'] = async (
    _nextTargetKeys: TransferProps['targetKeys'],
    direction: string
  ) => {
    // unlink 기능이 비활성화되어 있고 왼쪽으로 이동하는 경우 차단
    if (disableUnlink && direction === 'left') {
      return;
    }

    const isMovingRight = direction === 'right';
    const confirmMessage = isMovingRight
      ? 'Would you like to register the selected items?'
      : 'Would you like to unregister the selected items?';

    Modal.confirm({
      title: 'Confirm',
      content: confirmMessage,
      okText: 'Confirm',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          if (isMovingRight) {
            await dataHandler.createWithParentId(leftSelectedKeys as number[]);
            setLeftSelectedKeys([]);
            setShouldUpdateOrder(true);
            setRefreshKey(prev => prev + 1);
          } else {
            await dataHandler.deleteWithParentId(rightSelectedKeys as number[]);
            setTargetPage(1);
            setShouldUpdateOrder(true);
            setRefreshKey(prev => prev + 1);
          }
        } catch (error) {
          // message.error(`Failed to update the items`);
          console.error(error);
          setRefreshKey(prev => prev + 1);
        }
      },
    });
  };

  useEffect(() => {
    const currentSelectedKeys = leftSelectedKeys;
    const query = {
      page: sourcePage.toString(),
      page_size: pageSize.toString(),
      search: sourceSearchTerm,
      content_type: sourceContentType,
      order_by: sourceSortField?.toString() || 'id',
      order_direction: sourceOrder === 'ascend' ? 'asc' : 'desc',
    };
    setIsSourceLoading(true);
    dataHandler
      .fetchSourceData(query)
      .then(({ data, count }) => {
        setTransferDataSource(data);
        setTransferDataSourceTotal(count);
      })
      .finally(() => {
        setIsSourceLoading(false);
      });
    setLeftSelectedKeys(currentSelectedKeys);
  }, [
    sourcePage,
    sourceSearchTerm,
    pageSize,
    refreshKey,
    sourceContentType,
    sourceOrder,
    sourceSortField,
  ]);

  useEffect(() => {
    const currentSelectedKeys = rightSelectedKeys;
    const query = {
      page: targetPage.toString(),
      page_size: pageSize.toString(),
      search: targetSearchTerm,
      content_type: targetContentType,
      order_by: targetSortField.toString(),
      order_direction: targetOrder === 'ascend' ? 'asc' : 'desc',
    };
    setIsTargetLoading(true);
    dataHandler
      .fetchTargetData(query)
      .then(({ data, count }) => {
        setTargetSource(data);
        setTargetSourceTotal(count);
      })
      .finally(() => {
        setIsTargetLoading(false);
      });
    setTransferDataSource([...transferDataSource, ...targetSource]);
    setRightSelectedKeys(currentSelectedKeys);
  }, [
    targetPage,
    targetSearchTerm,
    pageSize,
    refreshKey,
    targetContentType,
    targetOrder,
    targetSortField,
  ]);

  // 에피소드 추가/삭제 후 순서 업데이트
  useEffect(() => {
    if (shouldUpdateOrder && targetSource.length > 0 && onOrderChangeHandler) {
      const updateOrder = async () => {
        try {
          // 전체 데이터를 가져와서 순서 업데이트
          const allDataQuery = {
            page: '1',
            page_size: '1000', // 충분히 큰 값으로 전체 데이터 가져오기
            search: targetSearchTerm,
            content_type: targetContentType,
            order_by: orderByField,
            order_direction: 'asc',
          };
          const { data: allData } = await dataHandler.fetchTargetData(allDataQuery);
          if (allData.length > 0 && onOrderChangeHandler) {
            const updatedOrders = allData.map(
              (item: TransferDataSourceInterface, index: number) => ({
                id: item.id,
                [orderFieldName]: index + 1,
              })
            );
            await onOrderChangeHandler(updatedOrders as { id: number; [key: string]: number }[]);
            // order 업데이트 후 target 데이터 refresh
            setRefreshKey(prev => prev + 1);
          }
        } catch (error) {
          console.error('Failed to update order:', error);
        } finally {
          setShouldUpdateOrder(false);
        }
      };
      updateOrder();
    }
  }, [
    shouldUpdateOrder,
    targetSource.length,
    targetSearchTerm,
    targetContentType,
    dataHandler,
    onOrderChangeHandler,
    orderFieldName,
    orderByField,
  ]);

  return (
    <div className={disableUnlink ? 'transfer-disabled' : ''}>
      <Transfer
        className="transfer-container"
        {...restProps}
        dataSource={transferDataSource.map(item => ({
          key: item.id,
          title: item.name,
          disabled: item.disabled,
        }))}
        targetKeys={targetSource.map(item => item.id)}
        selectedKeys={[...leftSelectedKeys, ...rightSelectedKeys]}
        onSearch={onSearchHandler}
        onChange={onChange}
        showSelectAll={false}
        selectAllLabels={[' ', ' ']}
        titles={[
          `${leftSelectedKeys.length}/${transferDataSourceTotal} Total Items`,
          `${rightSelectedKeys.length}/${targetSourceTotal} Registered Items`,
        ]}
      >
        {({ direction, onItemSelectAll, disabled: listDisabled }) => {
          const filteredDataSource = direction === 'left' ? transferDataSource : targetSource;
          const total = direction === 'left' ? transferDataSourceTotal : targetSourceTotal;
          const currentSelectedKeys = direction === 'left' ? leftSelectedKeys : rightSelectedKeys;
          const rowSelection: TableRowSelection<TransferItem> = {
            getCheckboxProps: () => ({ disabled: listDisabled }),
            onChange(selectedRowKeys) {
              const currentPageItems = filteredDataSource.map(item => item.id);

              if (direction === 'left') {
                // 현재 페이지에 없는 선택된 항목들 유지
                const otherPagesSelected = leftSelectedKeys.filter(
                  key => !currentPageItems.includes(key)
                );

                // 현재 페이지의 선택된 항목들만 포함
                const newLeftKeys = [
                  ...otherPagesSelected,
                  ...selectedRowKeys.filter(key => currentPageItems.includes(key as number)),
                ];

                setLeftSelectedKeys(newLeftKeys as number[]);

                // Transfer 내부 상태와 동기화
                const currentPageSelectedKeys = selectedRowKeys.filter(key =>
                  currentPageItems.includes(key as number)
                );
                onItemSelectAll(currentPageSelectedKeys, currentPageSelectedKeys.length > 0);
              } else {
                const otherPagesSelected = rightSelectedKeys.filter(
                  key => !currentPageItems.includes(key)
                );

                const newRightKeys = [
                  ...otherPagesSelected,
                  ...selectedRowKeys.filter(key => currentPageItems.includes(key as number)),
                ];
                setTransferDataSource([...transferDataSource, ...targetSource]);
                setRightSelectedKeys(newRightKeys as number[]);

                // Transfer 내부 상태와 동기화
                const currentPageSelectedKeys = selectedRowKeys.filter(key =>
                  currentPageItems.includes(key as number)
                );
                onItemSelectAll(currentPageSelectedKeys, currentPageSelectedKeys.length > 0);
              }
            },
            selectedRowKeys: currentSelectedKeys.filter(key =>
              filteredDataSource.some(item => item.id === key)
            ),
            selections: [Table.SELECTION_ALL, Table.SELECTION_INVERT, Table.SELECTION_NONE],
          };
          // 드래그 핸들 컬럼 제거 - 전체 행을 드래그 가능하게 함

          return (
            <>
              <TransferSearch
                direction={direction}
                handleSearch={onSearchHandler}
                filterOptions={filterOptions}
                onFilterChange={onFilterChange}
                filterValue={filterValue}
              />
              {direction === 'right' && orderOptions ? (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragStart={(event: DragStartEvent) => {
                    setActiveId(event.active.id as number);
                  }}
                  onDragEnd={(event: DragEndEvent) => {
                    setActiveId(null);
                    handleDragEnd(event, direction, filteredDataSource);
                  }}
                  onDragCancel={() => {
                    setActiveId(null);
                  }}
                >
                  <SortableContext
                    items={filteredDataSource.map(item => item.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <Table
                      loading={isTargetLoading}
                      rowSelection={rowSelection}
                      columns={columns}
                      dataSource={filteredDataSource}
                      size={size}
                      rowKey="id"
                      pagination={{
                        current: targetPage,
                        pageSize: pageSize,
                        total: total,
                      }}
                      onChange={(pagination, filters, sorter) => {
                        onPageChange(
                          direction,
                          pagination.current || 1,
                          pagination.pageSize || 10,
                          filters,
                          sorter as SorterResult<TransferDataSourceInterface>
                        );
                      }}
                      style={{ pointerEvents: listDisabled ? 'none' : undefined }}
                      components={{
                        body: {
                          row: (rowProps: any) => {
                            const rowId =
                              rowProps['data-row-key'] || rowProps.record?.id || rowProps.id;
                            return <SortableRow id={rowId} {...rowProps} />;
                          },
                        },
                      }}
                      onRow={() => ({
                        onClick: (): void => {
                          // onItemSelect(id, listSelectedKeys.includes(id));
                        },
                      })}
                    />
                  </SortableContext>
                  <DragOverlay>
                    {activeId ? (
                      <div className="drag-overlay-item">
                        <div className="drag-overlay-content">
                          {(() => {
                            const activeItem = filteredDataSource.find(
                              item => item.id === activeId
                            );
                            if (!activeItem) return null;
                            return (
                              <div className="drag-overlay-row">
                                {columns.map((col, idx) => {
                                  if ('children' in col) {
                                    // ColumnGroupType인 경우 스킵
                                    return null;
                                  }
                                  const dataIndex = (col as any).dataIndex as string;
                                  const value = dataIndex ? (activeItem as any)[dataIndex] : '';
                                  const renderedValue = (col as any).render
                                    ? (col as any).render(value, activeItem as any, idx)
                                    : String(value || '');
                                  return (
                                    <div key={idx} className="drag-overlay-cell">
                                      {renderedValue}
                                    </div>
                                  );
                                })}
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    ) : null}
                  </DragOverlay>
                </DndContext>
              ) : (
                <Table
                  loading={direction === 'left' ? isSourceLoading : isTargetLoading}
                  rowSelection={rowSelection}
                  columns={columns}
                  dataSource={filteredDataSource}
                  size={size}
                  pagination={{
                    current: direction === 'left' ? sourcePage : targetPage,
                    pageSize: pageSize,
                    total: total,
                    // onChange: (page, pageSize) => {
                    //   onPageChange(direction, page, pageSize);
                    // },
                  }}
                  onChange={(pagination, filters, sorter) => {
                    onPageChange(
                      direction,
                      pagination.current || 1,
                      pagination.pageSize || 10,
                      filters,
                      sorter as SorterResult<TransferDataSourceInterface>
                    );
                  }}
                  style={{ pointerEvents: listDisabled ? 'none' : undefined }}
                  onRow={() => ({
                    onClick: (): void => {
                      // onItemSelect(id, listSelectedKeys.includes(id));
                    },
                  })}
                />
              )}
            </>
          );
        }}
      </Transfer>
    </div>
  );
};

export default CustomTransfer;
